/**
 * Kicky Road — Stage 2 playable harness (juice + kick-leg integration).
 *
 * Stage 1c core loop + Stage 2 feel: event dispatcher, kick legs, screen
 * shake, FOV punch, hitstop zoom, crash sequence, score popups, particles,
 * speed lines, HUD portrait polish, near-field road fix.
 *
 * Loop: startLoop → fixed 60 Hz sim (capture prev poses + sim.step(input) +
 * event drain + camera rig step) → render (cameraRig.update + interpolated
 * road scroll + juice.update + vehicleView.sync (interpolated) +
 * riderCaster.update + renderer.render + hud.update). Phase
 * transitions: title→playing on first input (sim handles internally),
 * gameover→restart via the HUD RESTART button or Enter/click.
 *
 * Seed: `?seed=<n>` URL param pins the run (and every restart of it); with no
 * URL seed each run — first and every restart — draws a fresh random 32-bit
 * seed.
 */
import { startLoop } from "./core/loop";
import { initPhysics } from "./sim/physics";
import { createWorld } from "./sim/world";
import { TUNING } from "./sim/tuning";
import { InputCollector } from "./render/input";
import { World as RoadWorld } from "./render/world";
import { CameraRig } from "./render/cameraRig";
import { VehicleView } from "./render/vehicleView";
import { CoinView } from "./render/coins";
import { Hud } from "./render/hud";
import { EventDispatcher } from "./render/events";
import { JuiceCoordinator } from "./render/juiceCoordinator";
import { AssetManager } from "./render/assets";
import { createBiomeSystem } from "./render/biomes";
import { RiderCaster } from "./render/riderCaster";
import { GameAudio } from "./render/audio";
import { warmupPipelines } from "./render/warmup";
import { setEarthCurveOrigin, setEarthCurveRadius } from "./render/earthCurve";
import { setShadowDepthRange } from "./render/softShadow";
import { FrameStats } from "./render/frameStats";
import { WebGPURenderer, Scene, ACESFilmicToneMapping } from "three/webgpu";

// ---------------------------------------------------------------------------
// FPS meter — hand-rolled, no dependency. Reports a smoothed FPS readout.
// Moved INSIDE the 9:16 viewport (bottom-left, tiny, dev-only) in boot().
// ---------------------------------------------------------------------------
const fpsEl = document.getElementById("fps");
let fpsFrames = 0;
let fpsLastUpdate = performance.now();

function updateFps(now: number): void {
  if (fpsEl === null) return;
  fpsFrames++;
  if (now - fpsLastUpdate >= 250) {
    const fps = (fpsFrames * 1000) / (now - fpsLastUpdate);
    fpsLastUpdate = now;
    fpsFrames = 0;
    fpsEl.textContent = `${fps.toFixed(0)} fps`;
  }
}

// ---------------------------------------------------------------------------
// Seed: ?seed=<n> pins the run (debugging); otherwise EVERY run — including
// every restart — gets a fresh random seed, so dying never means replaying the
// exact road layout that just killed you.
// ---------------------------------------------------------------------------
function randomSeed(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}

/** The pinned `?seed=<n>` value, or null when the URL does not pin one. */
function parseSeed(): number | null {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("seed");
  if (raw !== null) {
    const n = Number.parseInt(raw, 10);
    if (Number.isFinite(n)) return n >>> 0;
  }
  return null;
}
/**
 * Frame-time recorder, opt-in via `?perf=1` (dev/QA only — the marks cost a
 * handful of `performance.now()` calls per frame, which is why they are not
 * always on). Exposed as `window.__perf`; `__perf.report()` returns the
 * percentile summary. See render/frameStats.ts.
 */
const perf = new URLSearchParams(window.location.search).has("perf")
  ? new FrameStats()
  : null;
if (perf) (window as unknown as Record<string, unknown>).__perf = perf;

const PINNED_SEED = parseSeed();
const SEED = PINNED_SEED ?? randomSeed();
/**
 * Seed for the NEXT run, handed to the HUD's restart path. `undefined` means
 * "keep the current seed" (World.reset's default), which is what a pinned
 * debug seed wants; otherwise every restart re-rolls.
 */
const nextSeed = (): number | undefined =>
  PINNED_SEED !== null ? undefined : randomSeed();

// ---------------------------------------------------------------------------
// Renderer + scene setup
// ---------------------------------------------------------------------------
const canvas = document.getElementById("gpu") as HTMLCanvasElement;
const renderer = new WebGPURenderer({ canvas, antialias: true });
// Toy-plastic look (DESIGN_SYSTEM v1.1): ACES filmic tone mapping rolls the
// clearcoat specular highlights off smoothly instead of clipping them to flat
// white discs, which is what makes a glossy surface read as moulded plastic
// rather than as a blown-out render. Exposure 1.1 buys back the mid-tone
// brightness ACES takes out. The third leg of the look (the per-biome gradient
// environment) is installed in render/world.ts `load()` — see
// render/envGradient.ts; the materials are in render/plastic.ts.
renderer.toneMapping = ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.1;

const scene = new Scene();
// --- HUD (DOM overlay) — created in boot() once the sim exists. ---------
let hud: Hud;
// --- Juice coordinator — created in boot() once the HUD root exists. ---
let juice: JuiceCoordinator;
// --- Rider shadow caster (invisible cyclist proxy) — created in boot() ---
let riderCaster: RiderCaster;
// --- Audio manager (Stage 6 WebAudio) — created in boot() after assets ---
let audio: GameAudio;
// Letterboxed 9:16 viewport pixel dimensions (tracked by resize).
let viewportW = 0;
let viewportH = 0;
const road = new RoadWorld();
scene.add(road.group);
scene.background = road.skyColor;
const rig = new CameraRig(window.innerWidth / window.innerHeight);
// Camera must be IN the scene graph or the renderer's traversal never visits
// its children (cockpit, wheel, kick legs would silently never draw).
scene.add(rig.root);
// --- Vehicle view (pooled fleet) — created in boot() after assets load ----
let vehicles: VehicleView;
// --- Coin field (one InstancedMesh over the sim's coin pool) --------------
const coinView = new CoinView();
scene.add(coinView.mesh);

// --- Keyboard input --------------------------------------------------------
const input = new InputCollector(window);


// ---------------------------------------------------------------------------
// Resize handling
// ---------------------------------------------------------------------------
function resize(): void {
  // Fit a 9:16 portrait viewport inside the window, centered (letterboxed).
  //
  // FIXED BENCH VIEWPORT + ZERO-SIZE GUARD (2026-08-02 wreck-spike pass).
  // `perfbench.html` sets `__BENCH_VIEWPORT` so a measurement run always draws
  // the same number of pixels no matter what the driving agent's browser pane
  // is doing. Without it the bench renders at `window.innerWidth`, and an
  // agent-driven pane is frequently 0 x 0 — which makes WebGPU fail to create
  // the swapchain texture EVERY FRAME and log three uncaptured
  // GPUValidationErrors (with stack capture) from inside the render submit.
  // That, not the game, produced the 47-70 ms "wreck-lifetime" frames this
  // pass was chasing: the spikes are pure error-reporting cost billed to the
  // `submit` phase. The Math.max(1, ...) floor is the same guard for the real
  // game — a minimised or hidden tab must not push a 0-sized swapchain either.
  const forced = (window as unknown as { __BENCH_VIEWPORT?: { w: number; h: number } })
    .__BENCH_VIEWPORT;
  const w = Math.max(1, forced ? forced.w : window.innerWidth);
  const h = Math.max(1, forced ? forced.h : window.innerHeight);
  const target = TUNING.view.aspectW / TUNING.view.aspectH;
  let vw = w;
  let vh = Math.round(w / target);
  if (vh > h) {
    vh = h;
    vw = Math.round(h * target);
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(vw, vh, true);
  rig.setAspect(vw / vh);
  // Track the letterboxed viewport size for juice systems (popups, speed lines).
  viewportW = vw;
  viewportH = vh;
  if (juice) juice.setViewportSize(vw, vh);
}
window.addEventListener("resize", resize);
resize();

// ---------------------------------------------------------------------------
/**
 * BOOT PROFILE (2026-08-02 load-time pass). Wall clock per boot phase, published
 * on `window.__bootProfile` and logged with `?perf=1`. Costs one
 * `performance.now()` per phase; it is what the load-time work is measured with.
 */
const bootProfile: Array<{ phase: string; ms: number; at: number }> = [];
let bootMarkT = performance.now();
function bootMark(label: string): void {
  const now = performance.now();
  bootProfile.push({ phase: label, ms: now - bootMarkT, at: now });
  bootMarkT = now;
  const m = (window as unknown as { __pipeMark?: (s: string) => void }).__pipeMark;
  if (typeof m === "function") m(label);
}

async function boot(): Promise<void> {
  // --- LOADING state: covers everything until assets are ready ------------
  //
  // OWNER BUG (2026-08-02): "When the game is loading, the compiling text
  // overlaps with the other elements."
  //
  // It did, and the cause was STACKING CONTEXTS, not spacing. This overlay used
  // to be a child of `#app`, which is `position: fixed` and therefore its own
  // stacking context with an AUTO z-index — so the overlay's `z-index: 50` only
  // ever ranked it against the canvas, inside that context. The HUD root
  // (render/hud.ts) is a child of `document.body` at `z-index: 10`, i.e. a
  // sibling of `#app` in the ROOT context, so it painted over the whole of
  // `#app` — overlay included. The title card, the top-left telemetry and the
  // #fps readout were all drawn on top of the opaque loading screen, and the
  // window where you actually SEE it is the 7-9 s pipeline warm-up ("COMPILING",
  // see render/warmup.ts), which is exactly what the owner caught.
  //
  // The fix is to put the overlay in the same stacking context as the HUD (the
  // root one) and rank it above: appended to `document.body`, z-index 100.
  // Nothing about the HUD changes, and any future overlay parented to the HUD
  // (juice popups, speed lines) is covered by the same ordering.
  const loadingEl = document.createElement("div");
  loadingEl.id = "loading";
  const loadingLabel = document.createElement("div");
  loadingLabel.textContent = "LOADING";
  loadingEl.appendChild(loadingLabel);
  loadingEl.style.cssText =
    "position:fixed;inset:0;display:flex;align-items:center;justify-content:center;" +
    "font:600 24px/1 ui-monospace,monospace;color:#cfe;letter-spacing:.3em;" +
    "background:#0b0b10;z-index:100;pointer-events:none;";
  document.body.appendChild(loadingEl);

  await renderer.init();
  bootMark("renderer.init");

  // Enable shadow maps (Stage 4d: rider shadow + sun caster).
  renderer.shadowMap.enabled = true;
  // The scene's ONE shadow-casting light overrides this with its own
  // `shadow.filterNode` (a 16-tap Vogel disk — see render/softShadow.ts and
  // render/riderCaster.ts `configureSun`), so this only decides the fallback.
  // It is left on PCFShadowMap (1) rather than PCFSoftShadowMap (2) because the
  // "soft" variant is a fixed one-texel-penumbra 2x2 gather that ignores
  // `shadow.radius` — i.e. the HARDEST of the three, despite the name.
  renderer.shadowMap.type = 1; // PCFShadowMap (fallback; the sun filters itself)

  // Initialize Rapier (proven load) + create the sim world.
  await initPhysics();
  const sim = await createWorld(SEED);
  bootMark("physics+world");

  // --- Stage 4 art: load GLBs + textures in parallel before the loop ---
  const assets = new AssetManager();
  // --- Stage 6 audio: load + decode wavs in parallel with the art ---
  audio = new GameAudio();
  // Road textures (asphalt + sky) load in parallel with the GLBs and audio.
  // The renderer backend is already initialized (above), so road.load also
  // bakes the PMREM image-based lighting environment from the sky pano.
  const [registry] = await Promise.all([
    assets.loadAll(),
    road.load(renderer, scene),
    audio.loadAll(),
  ]);
  bootMark("assets+road+audio");
  // Build the pooled vehicle view from the loaded fleet templates.
  vehicles = new VehicleView(
    TUNING.traffic.maxThreatVehicles,
    TUNING.traffic.maxAmbientVehicles,
    registry,
  );
  scene.add(vehicles.group);

  // Biome system: six rotating scenes (downtown -> bridge -> pine forest ->
  // wheat fields -> tunnel -> autumn city), owns fog + the sky background.
  // Seeded from the run seed so prop placement is deterministic per run.
  const biomes = await createBiomeSystem(registry, road, scene, sim.state.seed);
  bootMark("biomes");
  scene.add(biomes.group);
  scene.fog = biomes.fog;
  road.biomesHook = biomes;

  // Attach the cockpit GLB to the camera (swaps the placeholder bars).
  if (registry.cockpit) {
    rig.attachCockpit(registry.cockpit);
  }

  // Rider shadow: a REAL shadow. An invisible clone of the cyclist GLB sits
  // where the rider is and casts into the sun's shadow map; the sun + its
  // target are re-parented onto the camera rig's root (whose x IS the
  // interpolated player x), so both the caster and the tight shadow frustum
  // track the bike by construction.
  // The RIGGED cyclist (legs split into FK nodes) is the caster, so the shadow
  // can pedal; the crash cinematic keeps the un-rigged registry.cyclist. If the
  // rigged GLB failed to load, fall back to the plain one — a correct but
  // static shadow beats no shadow.
  riderCaster = new RiderCaster(
    rig,
    registry.cyclistRig ?? registry.cyclist,
    road.sunLight,
  );

  // Previous-tick player snapshot for camera + road + shadow interpolation.
  // Declared before the HUD so the restart hook below can re-base them.
  let prevPlayer = { ...sim.state.player };
  let prevDistance = sim.state.player.distance;

  /**
   * Re-base every render-interpolation snapshot onto the CURRENT sim state.
   * Called after sim.reset() (from the HUD's DOM handlers, which run between
   * rAF ticks) — without it, the render frames that run before the next sim
   * tick would interpolate between the old run's distance and 0, flashing the
   * road/biome at a mid-run position for a frame.
   */
  const rebaseInterpolation = (): void => {
    prevPlayer = { ...sim.state.player };
    prevDistance = sim.state.player.distance;
    rig.step(prevPlayer, sim.state.player);
    vehicles.step(sim.state); // snap vehicles' prev poses too
    // Park the shadow's legs back on the authored rest pose for the new run.
    riderCaster.reset();
  };

  // Create the HUD now that the sim exists. When the HUD consumes a Space
  // press for a phase action (start/restart/resume), clear the collector's
  // kick edge so that press never doubles as a kick on the next sim step.
  hud = new Hud(
    sim,
    canvas,
    () => input.clearKickEdge(),
    rebaseInterpolation,
    nextSeed,
  );
  // Move the #fps meter INSIDE the 9:16 viewport (bottom-left, tiny, dev-only).
  if (fpsEl) {
    hud.root.appendChild(fpsEl);
    fpsEl.style.position = "absolute";
    fpsEl.style.top = "auto";
    fpsEl.style.left = "6px";
    fpsEl.style.bottom = "4px";
    fpsEl.style.zIndex = "11";
  }
  // --- Stage 2 juice: event dispatcher + coordinator ---------------------
  // The HUD root is the letterboxed 9:16 viewport container; juice DOM
  // overlays (popups, speed lines, crash vignette) live inside it.
  const dispatcher = new EventDispatcher();
  juice = new JuiceCoordinator(hud.root, viewportW, viewportH);
  scene.add(juice.particles.root);
  // Load the kick-leg GLB (meshopt-compressed) + attach to the camera.
  try {
    await juice.load();
    juice.attachLegs(rig.camera);
  } catch (err) {
    // Leg load failure is non-fatal — the game runs without legs; the
    // other juice (shake, FOV, particles, popups) still fires.
    console.warn("Kick leg GLB failed to load:", err);
  }
  // Register all juice event handlers on the dispatcher.
  juice.register(dispatcher);
  juice.setViewportSize(viewportW, viewportH);
  // COINS: the magnet sprites fly to the HUD's coin counter and tick it as
  // they land (render/coinFlight.ts + hud.ts `bumpCoin`).
  juice.setCoinSink(hud);
  // Crash cinematic (3rd-person cyclist launch) — CrashCamAgent wiring.
  await juice.attachCinematic(scene, rig, registry.cyclist);
  bootMark("juice+cinematic");
  // --- Stage 6 audio: register unlock (gesture) + event handlers --------
  // The AudioContext is created lazily on the first user gesture (the same
  // pointerdown/keydown that flips title→playing). Kick/crash handlers are
  // wired through the dispatcher — the single sim fan-out point.
  audio.registerUnlock();
  audio.register(dispatcher);
  // Pause on blur/hidden (Stage 7): auto-pause a running game when the tab
  // loses focus or is backgrounded. Never auto-resumes — the user resumes
  // via the pause button or KeyP. input.ts already releases held keys on blur.
  const autoPause = (): void => {
    if (sim.state.phase === "playing") sim.pause();
  };
  window.addEventListener("blur", autoPause);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) autoPause();
  });

  // Debug hooks for the main agent's in-browser verification (opt-in only).
  if (new URLSearchParams(window.location.search).has("debug")) {
    (window as unknown as { __sim: typeof sim; __input: typeof input }).__sim = sim;
    (window as unknown as { __sim: typeof sim; __input: typeof input }).__input = input;
    (window as unknown as { __juice: typeof juice }).__juice = juice;
    (window as unknown as { __dispatch: typeof dispatcher }).__dispatch = dispatcher;
    const w = window as unknown as Record<string, unknown>;
    w.__caster = riderCaster;
    // Road + biome handles: let an art/QA pass scrub the world to an arbitrary
    // travel distance (`__road.updateWorld(600)`) and force a frame without
    // playing there. Needed because a backgrounded tab suspends rAF, which
    // suspends the whole loop.
    w.__road = road;
    w.__biomes = biomes;
    w.__vehicles = vehicles;
    w.__coins = coinView;
    w.__scene = scene;
    w.__camera = rig.camera;
    w.__renderer = renderer;
    w.__rig = rig;
    // Curved-earth bend (render/earthCurve.ts): QA can re-point the horizon or
    // retune/flatten the radius live without a rebuild.
    w.__earthCurve = { setOrigin: setEarthCurveOrigin, setRadius: setEarthCurveRadius };
    // Contact-hardening dial (render/softShadow.ts). QA can A/B the PCSS ramp
    // live: a huge depth range pins the filter at the full soft `shadow.radius`
    // (the pre-2026-08-02 behaviour), ~0 pins it at the crisp contact floor.
    w.__shadowDepthRange = setShadowDepthRange;
  }

  // Initial world scroll + camera placement.
  road.updateWorld(sim.state.player.distance);
  rig.step(prevPlayer, sim.state.player);

  // --- Pipeline warm-up (still under the LOADING overlay) ----------------
  // Compile every vehicle-paint, biome, particle and shadow pipeline NOW, so
  // the first car of a new model / the first metre of a new biome does not
  // stall a gameplay frame. See render/warmup.ts for the measurements.
  // Same single centred text block — only the word changes, so the overlay
  // never grows a second line that could collide with anything.
  loadingLabel.textContent = "COMPILING";
  const warmupT0 = performance.now();
  await warmupPipelines({
    renderer,
    scene,
    camera: rig.camera,
    vehicles,
    biomes,
    juice,
    coins: coinView,
  });
  bootMark("warmup");
  if (perf) {
    console.info(`[perf] pipeline warm-up: ${(performance.now() - warmupT0).toFixed(0)} ms`);
  }
  // The warm-up left the world posed for compiling; put it back on the run.
  road.updateWorld(sim.state.player.distance);

  // --- Assets ready: remove the LOADING overlay --------------------------
  loadingEl.remove();
  bootMark("overlay-dismissed");
  (window as unknown as Record<string, unknown>).__bootProfile = bootProfile;
  if (perf) {
    console.info(
      "[perf] boot profile:",
      bootProfile.map((b) => `${b.phase} ${b.ms.toFixed(0)}ms`).join(" | "),
      `TOTAL ${bootMarkT.toFixed(0)}ms`,
    );
  }

  // --- Fixed-step loop: sim @ 60 Hz, render every rAF with alpha --------
  let lastRenderTime = performance.now() / 1000;
  startLoop(
    (dt) => {
      // sim callback — fixed 60 Hz.
      void dt; // sim uses its own fixed dt internally.
      prevPlayer = { ...sim.state.player };
      prevDistance = sim.state.player.distance;

      // Capture the previous sim-step vehicle poses for render interpolation
      // (mirrors prevPlayer / cameraRig.step). Must run BEFORE sim.step.
      vehicles.step(sim.state);

      // Sample + consume input (kick edge fires exactly once per press).
      const sampled = input.sampleAndClear();

      sim.step(sampled);

      // --- Drain sim events to the juice dispatcher (between sim steps) --
      // The sim clears its events array at the START of the next doStep,
      // so they persist here. We fan them out to juice systems now, before
      // the next step overwrites them. This is also the future audio attach
      // point — every event leaves the sim through this single call.
      // Kick-window instrumentation: tag the wall-clock instant of every
      // connecting kick so `__perf.report()` can split frame times into
      // "within ±0.5 s of a kick" and "everything else". Without that split a
      // handful of 60 ms hitches vanishes into a healthy p99.
      if (perf) {
        for (let i = 0; i < sim.state.events.length; i++) {
          const ev = sim.state.events[i];
          const t = ev.type;
          if (t === "kick") {
            perf.markKick();
            // WRECK LIFETIME (2026-08-02 wreck-spike pass). The ±0.5 s kick
            // window closes long before the wreck does — it is still flying,
            // skidding and smoking seconds later — so the residual 47–70 ms
            // frames all read as "out of kick window" noise. Opening a span
            // here and closing it on `kickedVehicleDown` puts them in a bucket.
            perf.markWreckStart(ev.vehicleId);
            perf.markMoment("kick/convert");
          }
          // Spawn window (2026-08-01 spawn-clone pass): the frame a vehicle
          // enters the pool is the frame the view used to deep-clone a GLB
          // subtree. Marking it splits the report the same way kicks do.
          else if (t === "spawn") perf.markSpawn();
          else if (t === "kickedVehicleGroundHit") perf.markMoment("groundHit");
          // The crash is the other big one-shot render event in a run (ragdoll
          // body, cinematic camera, vignette, debris burst) and it is NOT
          // wreck-related — marking it keeps it from being mis-attributed to a
          // wreck that happened to be alive at the same moment.
          else if (t === "crash") perf.markMoment("crash");
          else if (t === "kickedVehicleDown") {
            perf.markMoment("wreckDown");
            perf.markWreckEnd(ev.vehicleId);
          }
        }
      }
      dispatcher.drain(sim.state.events);
      // Clear the events after draining. The sim clears its events at the
      // start of the next doStep, but during hitstop doStep isn't called —
      // without this clear, the same events would be re-drained each hitstop
      // step, causing duplicate juice triggers.
      sim.state.events.length = 0;

      // Advance the camera rig's sim-side state for interpolation.
      // Road scroll + rider shadow now happen in the render callback with an
      // interpolated distance so they stay smooth at any display refresh rate.
      rig.step(prevPlayer, sim.state.player);
    },
    (alpha) => {
      // render callback — runs every rAF, interpolates between ticks.
      if (perf) perf.beginFrame();
      const now = performance.now() / 1000;
      // Clamp the render delta: after a tab-background stretch or a long frame
      // hitch, a raw multi-second rdt would age every juice system (particles,
      // leg pokes, FOV punches) to completion in one frame. 100 ms keeps
      // catch-up smooth without visible time-skips.
      const rdt = Math.min(now - lastRenderTime, 0.1);
      lastRenderTime = now;
      // Belt-and-braces guard for any reset path that bypasses the HUD hook:
      // distance is monotonic within a run, so a decrease can only mean the
      // sim was reset. Snap rather than lerp across that discontinuity.
      if (sim.state.player.distance < prevDistance) rebaseInterpolation();
      // Interpolated player travel distance — shared by the camera curve yaw,
      // road scroll, vehicle curve offsets, and rider shadow so every render
      // consumer bends onto the curve from the same smooth distance value.
      const interpDist =
        prevDistance + (sim.state.player.distance - prevDistance) * alpha;
      rig.update(alpha, rdt, interpDist);
      if (perf) perf.mark("rig");
      // Scroll the road with the INTERPOLATED distance so the road surface
      // moves smoothly at any display refresh rate instead of stepping at the
      // 60 Hz sim rate. The biome hook (attached via road.biomesHook) is
      // driven inside updateWorld with the same interpolated distance.
      road.updateWorld(interpDist);
      if (perf) perf.mark("world");
      // Juice update: applies shake/FOV/dip additively to the camera AFTER
      // the rig has set its own roll/pitch/FOV, then updates legs/particles/
      // popups/speed-lines/crash-vignette.
      juice.update(sim.state, rig.camera, rdt);
      if (perf) perf.mark("juice");
      // Stage 6 audio: drive wind rate/gain, music duck/restore, whoosh
      // pass-detection each render frame.
      audio.update(sim.state, rdt);
      if (perf) perf.mark("audio");
      vehicles.sync(sim.state, alpha, interpDist);
      // Coins ride the same interpolated distance as the vehicles and the road,
      // so they bend onto the curve from exactly the same value.
      coinView.sync(sim.state, interpDist);
      // Rider shadow caster: lateral tracking is inherited from the rig root,
      // so this parks the proxy while the crash cam owns the camera and turns
      // the pedals from the rider's ground speed (frozen outside `playing`).
      if (riderCaster) {
        riderCaster.update(
          rdt,
          sim.state.player.forwardSpeed,
          sim.state.phase === "playing",
        );
      }
      if (perf) perf.mark("vehicles");
      // Curved-earth world bend: every world-space material drops its vertices
      // by d^2/2R around THIS point (render/earthCurve.ts). Written once per
      // frame, after every system that can move the camera (rig, juice shake,
      // crash cam) and before the draw, so the colour pass and the shadow pass
      // bend around exactly the same origin.
      rig.camera.updateWorldMatrix(true, false);
      setEarthCurveOrigin(rig.camera.matrixWorld.elements[12], rig.camera.matrixWorld.elements[14]);
      renderer.render(scene, rig.camera);
      if (perf) perf.mark("submit");
      hud.update(sim.state);
      if (perf) {
        perf.mark("hud");
        perf.endFrame();
      }
      updateFps(performance.now());
    },
  );
}

void boot();
